import { HttpClient } from '@adapters/HttpClient';
import { useAuthStore } from '@shared/stores/authStore';
import { PreviewShippingRuleResponse, PreviewShippingRuleSyntheticInput } from '../models/PreviewShippingRule';
import { RulesModel } from '../models/Rules';

export interface RulesFilters {
  searchTerm?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  team_id?: number;
}

export interface RulesPage {
  rules: RulesModel[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

export class RulesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getRulesPaginated(filters: RulesFilters = {}): Promise<RulesPage> {
    const { searchTerm = '', sortBy = 'id', sortDirection = 'desc', page = 1, limit = 15, team_id } = filters;

    // Obtener el team_id del usuario conectado si no se pasó uno explícitamente
    const currentTeamId = team_id ?? useAuthStore.getState().team?.id;

    const params: any = {
      ...(searchTerm && { search: searchTerm }),
      ...(sortBy && { order_by: sortBy }),
      ...(sortDirection && { order_direction: sortDirection }),
      page,
      per_page: limit,
      ...(currentTeamId && { team_id: currentTeamId }),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const raw = await this.httpClient.get<any>('/shipping-rules', params, {}, showGlobalLoading);
    const payload = Array.isArray(raw) ? raw[0] : raw;
    const { data: rules, meta } = payload;
    const currentPage = meta?.current_page;
    const lastPage = meta?.last_page;
    const total = meta?.total;

    return {
      rules,
      nextPage: currentPage < lastPage ? currentPage + 1 : undefined,
      hasMore: currentPage < lastPage,
      total,
    };
  }

  async getAll(): Promise<RulesModel[]> {
    const response = await this.getRulesPaginated();
    return response.rules;
  }

  /**
   * Obtiene una regla por su ID.
   */
  async getById(ruleId: number): Promise<RulesModel> {
    return this.httpClient.get<RulesModel>(`/shipping-rules/${ruleId}`);
  }

  /**
   * Activa o desactiva una regla según el flag `activate`.
   */
  async toggle(ruleId: number, activate: boolean): Promise<void> {
    const endpoint = activate ? `/shipping-rules/${ruleId}/activate` : `/shipping-rules/${ruleId}/desactivate`;
    await this.httpClient.put(endpoint, {});
  }

  /**
   * Elimina una regla por su ID.
   */
  async deleteRule(ruleId: number): Promise<void> {
    await this.httpClient.delete(`/shipping-rules/${ruleId}`);
  }

  /**
   * Reordena las reglas según el array de IDs en el orden deseado.
   * @param teamId Equipo hijo (cliente) cuando el reorder es en contexto 3PL, p. ej. /clients/:id/rules
   */
  async reorder(rulesOrder: number[], teamId?: number): Promise<void> {
    await this.httpClient.put('/shipping-rules/reorder', {
      rules_order: rulesOrder,
      ...(Number.isFinite(teamId) && (teamId as number) > 0 ? { team_id: teamId } : {}),
    });
  }

  /**
   * Actualiza una regla existente.
   * @param ruleId ID de la regla
   * @param payload Objeto con los datos a actualizar (ya formateado)
   */
  async updateRule(ruleId: number, payload: any): Promise<void> {
    await this.httpClient.put(`/shipping-rules/${ruleId}`, payload);
  }

  /**
   * Simula qué regla de envío activa haría match (misma lógica que productivo, solo lectura).
   * POST /shipping-rules/preview — `showGlobalLoading: false` para feedback en modal.
   */
  async previewShippingRule(body: PreviewShippingRuleSyntheticInput): Promise<PreviewShippingRuleResponse> {
    return this.httpClient.post<PreviewShippingRuleResponse>('/shipping-rules/preview', body, false);
  }
}
